import { format, startOfDay } from 'date-fns';
import { CalendarIcon, Info, X } from 'lucide-react';
import { Button } from '@r/components/ui/button.tsx';
import { Calendar } from '@r/components/ui/calendar.tsx';
import { Label } from '@r/components/ui/label.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '@r/components/ui/popover.tsx';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@r/components/ui/tooltip.tsx';
import { cn } from '@r/utils/utils.ts';
import type { WorkspaceScheduleState } from '@admin/pages/personalization/content-block-workspace-schedule.ts';

const dateTriggerClassName =
  'h-11 rounded-xl border-border/70 bg-muted/25 shadow-none focus-visible:ring-primary/30';

interface ContentBlockSchedulingFieldsProps {
  schedule: WorkspaceScheduleState;
  onChange: (schedule: WorkspaceScheduleState) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}

/** Dates startAt / endAt du workspace (hors react-hook-form slide). */
export const ContentBlockSchedulingFields = ({
  schedule,
  onChange,
  t
}: ContentBlockSchedulingFieldsProps) => {
  const { startAt, endAt } = schedule;
  const scheduleHint = t('admin.contentblock.schedule.description');
  const today = startOfDay(new Date());

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-3">
        <div className="inline-flex min-h-[14px] items-center gap-1">
          <span className="text-[11px] font-bold uppercase leading-[14px] tracking-wider text-muted-foreground">
            {t('admin.contentblock.schedule.title')}
          </span>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="-m-0.5 inline-flex shrink-0 items-center justify-center rounded-full p-0.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-label={scheduleHint}
              >
                <Info className="w-3 h-3" strokeWidth={2.25} aria-hidden />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-sm text-xs leading-relaxed">
              {scheduleHint}
            </TooltipContent>
          </Tooltip>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <ScheduleDateField
            id="content-block-schedule-start"
            label={t('admin.contentblock.schedule.start')}
            emptyLabel={t('admin.contentblock.schedule.start.empty')}
            value={startAt}
            onSelect={(date) => onChange({ ...schedule, startAt: date ?? null })}
            onClear={() => onChange({ ...schedule, startAt: null })}
            disabled={(date) => {
              const day = startOfDay(date);
              if (day < today) return true;
              return endAt ? day >= startOfDay(endAt) : false;
            }}
          />
          <ScheduleDateField
            id="content-block-schedule-end"
            label={t('admin.contentblock.schedule.end')}
            emptyLabel={t('admin.contentblock.schedule.end.empty')}
            value={endAt}
            onSelect={(date) => onChange({ ...schedule, endAt: date ?? null })}
            onClear={() => onChange({ ...schedule, endAt: null })}
            disabled={(date) => (startAt ? date <= startOfDay(startAt) : false)}
          />
        </div>
      </div>
    </TooltipProvider>
  );
};

const ScheduleDateField = ({
  id,
  label,
  emptyLabel,
  value,
  onSelect,
  onClear,
  disabled
}: {
  id: string;
  label: string;
  emptyLabel: string;
  value: Date | null;
  onSelect: (date: Date | undefined) => void;
  onClear: () => void;
  disabled: (date: Date) => boolean;
}) => (
  <div className="space-y-2">
    <Label
      htmlFor={id}
      className="mb-0 inline pl-0 text-[11px] font-bold uppercase leading-[14px] tracking-wider text-muted-foreground"
    >
      {label}
    </Label>
    <div className="flex gap-2 items-center">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            className={cn(
              dateTriggerClassName,
              'flex-1 justify-start px-4 font-normal',
              !value && 'text-muted-foreground'
            )}
          >
            {value ? format(value, 'dd/MM/yyyy') : emptyLabel}
            <CalendarIcon className="ml-auto w-4 h-4 opacity-60" aria-hidden />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-auto" align="start">
          <Calendar
            mode="single"
            selected={value ?? undefined}
            onSelect={onSelect}
            disabled={disabled}
          />
        </PopoverContent>
      </Popover>
      {value ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          rounded="full"
          className="w-11 h-11 shrink-0 text-muted-foreground"
          aria-label={emptyLabel}
          onClick={onClear}
        >
          <X className="w-4 h-4" aria-hidden />
        </Button>
      ) : null}
    </div>
  </div>
);
